import { Link, useParams, useNavigate } from 'react-router-dom'
import { useTemplate, useDeleteTemplate } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { I18nTranslationsCard } from '../components/I18nFieldsSection'
import { Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'

export function TemplateDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: template, isLoading, error } = useTemplate(id!)
  const deleteMutation = useDeleteTemplate()

  const handleDelete = async () => {
    if (!confirm('이 템플릿을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return

    try {
      await deleteMutation.mutateAsync(id!)
      toast.success('템플릿이 삭제되었습니다')
      navigate('/admin/templates')
    } catch (error: unknown) {
      toast.error(`삭제 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`)
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  if (!template) return null

  return (
    <div className="max-w-4xl space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin">어드민</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/templates">템플릿</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>상세</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">{template.name}</h1>
          {template.description && (
            <p className="text-caption mt-2 leading-relaxed">{template.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Link to={`/admin/templates/${id}/edit`}>
            <Button>
              <Pencil className="w-4 h-4 mr-2" />
              수정
            </Button>
          </Link>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            삭제
          </Button>
        </div>
      </div>

      {/* Main Info */}
      <div className="bg-white rounded-lg border p-6 space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">템플릿 카테고리</label>
            <div className="mt-1 text-gray-900">{template.category?.name || '-'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">스타일</label>
            <div className="mt-1 text-gray-900">{template.style?.name || '-'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">비율</label>
            <div className="mt-1 text-gray-900">{template.aspectRatio}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">모델</label>
            <div className="mt-1 text-gray-900">{template.modelId || '기본 모델'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">정렬 순서</label>
            <div className="mt-1 text-gray-900">{template.listOrder}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">상태</label>
            <div className="mt-1">
              <Badge variant={template.enabled ? 'success' : 'secondary'}>
                {template.enabled ? '활성' : '비활성'}
              </Badge>
            </div>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">프롬프트</label>
          <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap font-mono text-sm">
            {template.textPrompt}
          </div>
        </div>

        {template.promptSuffix && (
          <div>
            <label className="text-sm font-medium text-gray-700">프롬프트 접미사</label>
            <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap font-mono text-sm">
              {template.promptSuffix}
            </div>
          </div>
        )}

        {template.thumbnailUrl && (
          <div>
            <label className="text-sm font-medium text-gray-700">썸네일</label>
            <div className="mt-1">
              <img
                src={`${template.thumbnailUrl}?v=${new Date(template.updatedAt).getTime()}`}
                alt={template.name}
                className="max-w-md rounded-lg border"
              />
            </div>
          </div>
        )}

        {template.references && template.references.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700">
              레퍼런스 이미지 ({template.references.length}개)
            </label>
            <div className="mt-2 grid grid-cols-4 gap-4">
              {template.references.map((ref) => (
                <div key={ref.id} className="border rounded-lg overflow-hidden bg-gray-50">
                  <img
                    src={`${ref.thumbnailUrl}?v=${new Date(ref.createdAt).getTime()}`}
                    alt="Reference"
                    className="w-full h-32 object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Translations */}
      <I18nTranslationsCard nameI18n={template.nameI18n} descriptionI18n={template.descriptionI18n} />
    </div>
  )
}
